import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { toast } from '@workspace/ui/components/sonner';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '~/auth/use-auth';
import { type AuthenticatedBloque, bloque } from '~/libs/bloque';
import { getDecimalsFromAssetPair } from '~/utils/currency';

type TopUpToAsset = 'DUSD/6' | 'COPM/2';
type UserLegalIdType = 'CC' | 'NIT' | 'CE';

type FindRatesResponse = Awaited<
  ReturnType<AuthenticatedBloque['swap']['findRates']>
>;
type SwapRate = FindRatesResponse['rates'][number];
type PseBanksResponse = Awaited<
  ReturnType<AuthenticatedBloque['swap']['pse']['banks']>
>;
type PseBank = PseBanksResponse['banks'][number];
type PseCreateResponse = Awaited<
  ReturnType<AuthenticatedBloque['swap']['pse']['create']>
>;

const ASSET_LABELS: Record<TopUpToAsset, string> = {
  'DUSD/6': 'USD',
  'COPM/2': 'Pesos',
};

const formatAssetAmount = (rawAmount: string | number, asset: string) => {
  const decimals = getDecimalsFromAssetPair(asset);
  const normalized = Number(rawAmount) / 10 ** decimals;
  return Number.isFinite(normalized) ? normalized.toFixed(decimals) : rawAmount;
};

const getAssetLabel = (asset: string) =>
  asset in ASSET_LABELS
    ? ASSET_LABELS[asset as TopUpToAsset]
    : asset.split('/')[0] || asset;

const formatRemainingTime = (remainingMs: number) => {
  if (remainingMs <= 0) return 'Expirado';
  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

interface PseTopUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  urn: string;
}

export function PseTopUpDialog({
  open,
  onOpenChange,
  urn,
}: PseTopUpDialogProps) {
  const { user } = useAuth();

  const [selectedTopUpAsset, setSelectedTopUpAsset] =
    useState<TopUpToAsset>('DUSD/6');
  const [amountCop, setAmountCop] = useState('10000');
  const [rates, setRates] = useState<SwapRate[]>([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [selectedRateSig, setSelectedRateSig] = useState('');
  const [banks, setBanks] = useState<PseBank[]>([]);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [selectedBankCode, setSelectedBankCode] = useState('');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [paymentRedirectUrl, setPaymentRedirectUrl] = useState('');
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [payerEmail, setPayerEmail] = useState('');
  const [payerFullName, setPayerFullName] = useState('');
  const [payerPhone, setPayerPhone] = useState('');
  const [payerLegalIdType, setPayerLegalIdType] =
    useState<UserLegalIdType>('CC');
  const [payerLegalId, setPayerLegalId] = useState('');
  const [payerUserType, setPayerUserType] = useState<'natural' | 'juridical'>(
    'natural',
  );

  const amountSrc = useMemo(() => {
    const numeric = Number(amountCop);
    if (!Number.isFinite(numeric) || numeric <= 0) return '';
    return Math.floor(numeric * 100).toString();
  }, [amountCop]);

  const selectedRate = useMemo(
    () => rates.find((rate) => rate.sig === selectedRateSig),
    [rates, selectedRateSig],
  );

  const selectedRateRemainingMs = selectedRate
    ? Number(selectedRate.until) - nowMs
    : 0;
  const isSelectedRateExpired = !!selectedRate && selectedRateRemainingMs <= 0;

  const canCreateOrder =
    !!selectedRateSig &&
    !!selectedBankCode &&
    !!amountSrc &&
    !!payerEmail.trim() &&
    !!payerFullName.trim() &&
    !!payerPhone.trim() &&
    !!payerLegalId.trim() &&
    !isSelectedRateExpired;

  const resetPseState = () => {
    setRates([]);
    setSelectedRateSig('');
    setBanks([]);
    setSelectedBankCode('');
    setPaymentRedirectUrl('');
  };

  useEffect(() => {
    if (!open || rates.length === 0) return;
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [open, rates.length]);

  useEffect(() => {
    if (!open) return;
    setPayerEmail(user?.profile?.email ?? '');
    setPayerFullName(user?.profile?.full_name ?? '');
    setPayerPhone(user?.profile?.phone ?? '');
    setPayerLegalIdType('CC');
    setPayerLegalId('');
    setPayerUserType('natural');
  }, [open, user]);

  const handleFetchRates = async () => {
    if (!amountSrc) {
      toast.error('Ingresa un monto en COP válido');
      return;
    }
    setIsLoadingRates(true);
    setSelectedRateSig('');
    setBanks([]);
    setSelectedBankCode('');
    setPaymentRedirectUrl('');
    try {
      const response = (await bloque.swap.findRates({
        fromAsset: 'COP/2',
        toAsset: selectedTopUpAsset,
        fromMediums: ['pse'],
        toMediums: ['kusama'],
        amountSrc,
      })) as FindRatesResponse;
      if (response.rates.length === 0) {
        throw new Error(
          'No hay rates disponibles para los activos/medios seleccionados.',
        );
      }
      setRates(response.rates);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'No se pudieron obtener rates';
      toast.error(message);
      setRates([]);
    } finally {
      setIsLoadingRates(false);
    }
  };

  const handleRateSelect = async (rateSig: string) => {
    const pickedRate = rates.find((rate) => rate.sig === rateSig);
    if (pickedRate && Number(pickedRate.until) <= Date.now()) {
      toast.error('Este rate ya expiró. Consulta rates nuevamente.');
      return;
    }
    setSelectedRateSig(rateSig);
    setSelectedBankCode('');
    setPaymentRedirectUrl('');
    setIsLoadingBanks(true);
    try {
      const response = (await bloque.swap.pse.banks()) as PseBanksResponse;
      if (!response.banks.length)
        throw new Error('No hay bancos disponibles para PSE.');
      setBanks(response.banks);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'No se pudieron cargar bancos';
      toast.error(message);
      setBanks([]);
    } finally {
      setIsLoadingBanks(false);
    }
  };

  const handleCreatePseOrder = async () => {
    if (!selectedRateSig) {
      toast.error('Selecciona un rate');
      return;
    }
    if (!selectedBankCode) {
      toast.error('Selecciona un banco');
      return;
    }
    if (!amountSrc) {
      toast.error('Ingresa un monto válido');
      return;
    }
    if (isSelectedRateExpired) {
      toast.error('El rate seleccionado expiró. Consulta rates nuevamente.');
      return;
    }
    if (!payerEmail.trim() || !payerFullName.trim() || !payerPhone.trim()) {
      toast.error('Completa los datos del pagador');
      return;
    }
    if (!payerLegalId.trim()) {
      toast.error('Ingresa el número de documento');
      return;
    }

    setIsCreatingOrder(true);
    setPaymentRedirectUrl('');
    try {
      const response = (await bloque.swap.pse.create({
        rateSig: selectedRateSig,
        toMedium: 'kusama',
        amountSrc,
        depositInformation: { urn },
        args: {
          bankCode: selectedBankCode,
          // @ts-expect-error
          userType: payerUserType,
          customerEmail: payerEmail.trim(),
          userLegalIdType: payerLegalIdType,
          userLegalId: payerLegalId.trim(),
          customerData: {
            fullName: payerFullName.trim(),
            phoneNumber: payerPhone.trim(),
          },
        },
      })) as PseCreateResponse;

      toast.success(`Orden creada: ${response.order?.id ?? 'sin id'}`);
      const redirectUrl = response.execution?.result?.how?.url;
      if (redirectUrl) {
        setPaymentRedirectUrl(redirectUrl);
        const openedWindow = window.open(
          redirectUrl,
          '_blank',
          'noopener,noreferrer',
        );
        if (openedWindow) {
          resetPseState();
          onOpenChange(false);
        }
      }
    } catch (error) {
      const rawMessage =
        error instanceof Error ? error.message : 'No se pudo crear la orden';
      const message = rawMessage.includes('E_RATE_EXPIRED')
        ? 'El rate expiró justo antes de crear la orden. Consulta rates de nuevo.'
        : rawMessage;
      toast.error(message);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetPseState();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Recargar tarjeta por PSE</DialogTitle>
          <DialogDescription>
            Elige activo destino, consulta rates disponibles, selecciona banco y
            crea la orden.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <p className="text-sm font-medium">Activo destino</p>
              <Select
                value={selectedTopUpAsset}
                onValueChange={(value) => {
                  setSelectedTopUpAsset(value as TopUpToAsset);
                  resetPseState();
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona activo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DUSD/6">
                    {ASSET_LABELS['DUSD/6']}
                  </SelectItem>
                  <SelectItem value="COPM/2">
                    {ASSET_LABELS['COPM/2']}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Monto (COP)</p>
              <Input
                type="number"
                min="1"
                step="1"
                value={amountCop}
                onChange={(e) => {
                  setAmountCop(e.target.value);
                  resetPseState();
                }}
                placeholder="10000"
              />
            </div>
          </div>

          <Button onClick={handleFetchRates} disabled={isLoadingRates}>
            {isLoadingRates ? 'Buscando rates...' : 'Consultar rates'}
          </Button>

          {rates.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Rates disponibles</p>
              <div className="grid gap-2">
                {rates.map((rate) => {
                  const isSelected = selectedRateSig === rate.sig;
                  const remainingMs = Number(rate.until) - nowMs;
                  const isExpired = remainingMs <= 0;
                  return (
                    <button
                      key={rate.sig}
                      type="button"
                      onClick={() => handleRateSelect(rate.sig)}
                      disabled={isExpired}
                      className={`rounded-md border p-3 text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : isExpired
                            ? 'border-border opacity-60'
                            : 'border-border hover:bg-accent'
                      }`}
                    >
                      <p className="text-sm font-medium">
                        {formatAssetAmount(rate.rate[0], rate.edge[0])}{' '}
                        {getAssetLabel(rate.edge[0])} -&gt;{' '}
                        {formatAssetAmount(rate.rate[1], rate.edge[1])}{' '}
                        {getAssetLabel(rate.edge[1])}
                      </p>
                      <p className="text-xs">
                        Vigencia: {formatRemainingTime(remainingMs)}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {rate.sig}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {selectedRateSig && (
            <div className="space-y-3">
              <div className="rounded-md border p-3">
                <p className="text-sm">
                  Tiempo restante del rate seleccionado:{' '}
                  <span className="font-semibold">
                    {formatRemainingTime(selectedRateRemainingMs)}
                  </span>
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Banco PSE</p>
                <Select
                  value={selectedBankCode}
                  onValueChange={setSelectedBankCode}
                  disabled={
                    isLoadingBanks ||
                    banks.length === 0 ||
                    isSelectedRateExpired
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        isLoadingBanks
                          ? 'Cargando bancos...'
                          : 'Selecciona banco'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((bank) => (
                      <SelectItem key={bank.code} value={bank.code}>
                        {bank.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Tipo de usuario</p>
                  <Select
                    value={payerUserType}
                    onValueChange={(v) =>
                      setPayerUserType(v as 'natural' | 'juridical')
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="natural">Natural</SelectItem>
                      <SelectItem value="juridical">Jurídico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Tipo de documento</p>
                  <Select
                    value={payerLegalIdType}
                    onValueChange={(v) =>
                      setPayerLegalIdType(v as UserLegalIdType)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona tipo de documento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CC">CC</SelectItem>
                      <SelectItem value="NIT">NIT</SelectItem>
                      <SelectItem value="CE">CE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Documento</p>
                  <Input
                    value={payerLegalId}
                    onChange={(e) => setPayerLegalId(e.target.value)}
                    placeholder="Número de documento"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Email</p>
                  <Input
                    type="email"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                    placeholder="correo@dominio.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Nombre completo</p>
                  <Input
                    value={payerFullName}
                    onChange={(e) => setPayerFullName(e.target.value)}
                    placeholder="Nombre y apellido"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Teléfono</p>
                  <Input
                    value={payerPhone}
                    onChange={(e) => setPayerPhone(e.target.value)}
                    placeholder="3001234567"
                  />
                </div>
              </div>
            </div>
          )}

          {paymentRedirectUrl && (
            <div className="rounded-md border p-3">
              <p className="text-sm">
                Enlace de pago generado. Si no se abrió automáticamente, usa el
                botón:
              </p>
              <Button
                className="mt-2"
                variant="outline"
                onClick={() => {
                  const openedWindow = window.open(
                    paymentRedirectUrl,
                    '_blank',
                    'noopener,noreferrer',
                  );
                  if (openedWindow) {
                    resetPseState();
                    onOpenChange(false);
                  }
                }}
              >
                Abrir enlace PSE
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetPseState();
              onOpenChange(false);
            }}
            disabled={isCreatingOrder}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreatePseOrder}
            disabled={isCreatingOrder || !canCreateOrder}
          >
            {isCreatingOrder ? 'Creando orden...' : 'Crear orden PSE'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
